// Answer Assist - Safeguarding gate.
//
// Implements Layer 2 of the spec (see docs/Technical Specification.md section 5).
// Every captured image flows through this function BEFORE it is saved, displayed back to the user,
// or sent to the cloud. The image is rejected if:
//   - a human face is detected
//   - more than 25 percent of the pixels are skin-tone
//   - fewer than 10 readable text characters are detected
//   - the image is too dark, too bright, or too blurry to read
//
// v0 status: the structure is in place. Some checks are heuristic only for now.
// Real on-device face detection requires a Capacitor plugin wrapping Apple Vision Framework
// (iOS) and Google ML Kit (Android). Wired in v0.2 alongside the vision pipeline.
//
// IMPORTANT: This file must remain content-free. No image bytes leave it.

export type SafeguardingReason =
  | 'face_detected'
  | 'too_much_skin'
  | 'no_text_found'
  | 'low_quality';

export type SafeguardingResult =
  | { ok: true }
  | { ok: false; reason: SafeguardingReason };

export async function runSafeguardingGate(dataUrl: string): Promise<SafeguardingResult> {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { ok: true }; // Fail-open ONLY when we genuinely cannot inspect.
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Layer 2a: quality check (luminance, dynamic range).
  const quality = analyseQuality(imageData);
  if (!quality.ok) return { ok: false, reason: 'low_quality' };

  // Layer 2b: skin-tone area check (heuristic catch for bodies and limbs).
  // Threshold tuned to still catch a face/body shot but tolerate workbook pages with
  // pink/peach highlighted boxes, coloured illustrations, or warm paper tints.
  const skinRatio = estimateSkinRatio(imageData);
  if (skinRatio > 0.4) {
    return { ok: false, reason: 'too_much_skin' };
  }

  // Layer 2c: on-device face detection.
  // Wired in v0.2 via a Capacitor plugin wrapping Apple Vision Framework (iOS) and ML Kit (Android).
  // For v0 we use the modern browser FaceDetector API where available (Chrome on Android, some iOS Safari builds).
  const faceDetected = await detectFaceIfAvailable(img);
  if (faceDetected) return { ok: false, reason: 'face_detected' };

  // Layer 2d: minimum text content check.
  // Wired in v0.2 with on-device OCR (Apple Vision, ML Kit). For now we only run this if the
  // browser exposes the experimental TextDetector API.
  const hasText = await detectTextIfAvailable(img);
  if (hasText === false) return { ok: false, reason: 'no_text_found' };

  return { ok: true };
}

// ----- helpers -----

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function analyseQuality(image: ImageData): { ok: boolean } {
  // Compute mean luminance, standard deviation, and the share of "dark ink" pixels.
  // What we actually need to know: is there enough dark text on light paper to read?
  // Originally the upper-bound on mean luminance was 240 - this rejected freshly printed
  // CGP / Letts / textbook pages, screenshots, and clean scans because they have very few
  // grey pixels. Real-world workbook pages (June 2026) regularly come in at mean 240-250.
  // New rule:
  //   - There must be SOME dark ink: at least 1.5% of sampled pixels with luma below 100.
  //   - Mean luminance must be > 40 (otherwise the image is a black photo).
  //   - Standard deviation must be > 15 (catches a flat, blurry, no-contrast photo).
  // We no longer reject for "too bright" alone - if there's ink to read, accept it.
  const data = image.data;
  let total = 0;
  let darkPixels = 0;
  const stride = Math.max(1, Math.floor((data.length / 4) / 5000));
  const luminances: number[] = [];
  for (let i = 0; i < data.length; i += stride * 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    luminances.push(luma);
    total += luma;
    if (luma < 100) darkPixels++;
  }
  const mean = total / luminances.length;
  let variance = 0;
  for (const l of luminances) {
    variance += (l - mean) ** 2;
  }
  const std = Math.sqrt(variance / luminances.length);
  const darkRatio = darkPixels / luminances.length;
  const ok = mean > 40 && std > 15 && darkRatio >= 0.015;
  return { ok };
}

function estimateSkinRatio(image: ImageData): number {
  // Simple skin-tone heuristic in RGB space. Not a face detector. It catches large areas
  // of human skin (arms, faces, torso) that would otherwise sneak past on a device without
  // a real face detector available.
  const data = image.data;
  let skinPixels = 0;
  let totalSampled = 0;
  const stride = Math.max(1, Math.floor(data.length / 4 / 5000));
  for (let i = 0; i < data.length; i += stride * 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (isSkinTone(r, g, b)) skinPixels++;
    totalSampled++;
  }
  return totalSampled === 0 ? 0 : skinPixels / totalSampled;
}

function isSkinTone(r: number, g: number, b: number): boolean {
  // Conservative rule that catches a wide range of skin tones without flagging cream paper.
  // Cream and off-white paper is high R, high G, high B. Skin tends to have red dominance and
  // a smaller spread between channels.
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max < 95) return false; // Too dark to be skin under normal lighting.
  if (max - min < 15) return false; // Greyscale or near-white. Paper, not skin.
  if (r <= g || r <= b) return false; // Skin has red dominance.
  if (Math.abs(r - g) < 15) return false; // Probably paper, not skin.
  if (b > r) return false;
  return true;
}

async function detectFaceIfAvailable(img: HTMLImageElement): Promise<boolean> {
  // The experimental Shape Detection API is present in some browsers. We use it where available;
  // when not available we rely on the skin-ratio heuristic above plus the v0.2 native plugin.
  const FaceDetector = (window as unknown as { FaceDetector?: new (opts?: object) => { detect: (img: HTMLImageElement) => Promise<unknown[]> } }).FaceDetector;
  if (!FaceDetector) return false;
  try {
    const detector = new FaceDetector({ fastMode: true });
    const faces = await detector.detect(img);
    return Array.isArray(faces) && faces.length > 0;
  } catch {
    return false;
  }
}

async function detectTextIfAvailable(img: HTMLImageElement): Promise<boolean | null> {
  // Returns null when we cannot tell. The caller treats null as "do not block on this check".
  const TextDetector = (window as unknown as { TextDetector?: new () => { detect: (img: HTMLImageElement) => Promise<{ rawValue?: string }[]> } }).TextDetector;
  if (!TextDetector) return null;
  try {
    const detector = new TextDetector();
    const blocks = await detector.detect(img);
    const totalChars = blocks.reduce((sum, b) => sum + ((b.rawValue ?? '').length), 0);
    return totalChars >= 10;
  } catch {
    return null;
  }
}
